import { PencilLineIcon } from 'lucide-react';

import {
  QuestionMarkedDescription,
  QuestionMarkedHeader,
  QuestionMarkedNoAnswer,
  QuestionMarkedQuestionContent,
  QuestionMarkedUserAnswer,
  QuestionMarkedWrapper,
} from '@/components/features/quiz/question/question-marked';
import { QuestionMatchingAnswer } from '@/components/features/quiz/question/question-resolver/question-matching';
import { Button } from '@/components/ui/button';
import { Collapsible, CollapsibleContent } from '@/components/ui/collapsible';
import { CorrectIcon } from '@/components/ui/quiz';
import { useQuestionMarkedForm, useQuestionMarkedMatching } from '@/hooks/questions';

import type { QuestionMarkedMatchingProps } from '.';

import { QuestionMarkedMatchingForm } from '.';

export function QuestionMarkedMatching(questionMarkedMatchingProps: QuestionMarkedMatchingProps) {
  const { question, marked, answer, showMarked, showQuestion, onEditMark } = questionMarkedMatchingProps;
  const { formRef, wrapperRef, openForm, setOpenForm, openFormHandler, closeFormHandler } = useQuestionMarkedForm();

  const { columns, score, totalPoints, correct, isMarked, correctState, questionObj } = useQuestionMarkedMatching(question, marked, answer);

  if (!showMarked) return null;

  if (!marked || !isMarked) return <QuestionMarkedNoAnswer showMarked={showMarked} />;

  return (
    <QuestionMarkedWrapper showMarked={showMarked} ref={wrapperRef}>
      <QuestionMarkedHeader
        score={score}
        totalPoints={totalPoints}
        correct={correct}
        correctType={correctState}
        headerActions={
          onEditMark && (
            <Button
              variantColor={correctState === 'waiting' ? 'destructive' : 'default'}
              variant={correctState === 'waiting' ? 'secondary' : 'tertiary'}
              size="sm"
              disabled={openForm}
              onClick={openFormHandler}
            >
              <PencilLineIcon />
              Oceń
            </Button>
          )
        }
      />
      <QuestionMarkedQuestionContent showQuestion={showQuestion} content={question.content}>
        <div className="space-y-2">
          {columns.map((column, index) => (
            <div key={index} className="grid grid-cols-[1fr_1fr] gap-1.5">
              {column.map((answer) => (
                <QuestionMatchingAnswer key={answer.id} content={answer.content} isSelected={false} isDisabled={false} pairNumber={null} className="cursor-default" />
              ))}
            </div>
          ))}
        </div>
      </QuestionMarkedQuestionContent>
      {marked.answers && !openForm && (
        <QuestionMarkedUserAnswer>
          <div className="space-y-3">
            {marked.answers.map((answer, index) => (
              <div key={index} className="space-y-2">
                <div className={'flex items-center gap-1'}>
                  <CorrectIcon answerState={answer.correct ?? null} />
                  <p className="text-muted-foreground text-sm">{answer.explanation ?? 'Brak wyjaśnienia'}</p>
                </div>
                <div className="grid grid-cols-2 gap-1.5">
                  <QuestionMatchingAnswer
                    content={question.answers.find((a) => a.left.id === answer.match?.left)?.left.content ?? ''}
                    correct={answer.correct}
                    isSelected={false}
                    isDisabled={false}
                    pairNumber={null}
                  />
                  <QuestionMatchingAnswer
                    content={question.answers.find((a) => a.right.id === answer.match?.right)?.right.content ?? ''}
                    correct={answer.correct}
                    isSelected={false}
                    isDisabled={false}
                    pairNumber={null}
                  />
                </div>
              </div>
            ))}
          </div>
        </QuestionMarkedUserAnswer>
      )}
      <Collapsible open={openForm} onOpenChange={setOpenForm} className="-my-px">
        <CollapsibleContent ref={formRef}>
          <QuestionMarkedDescription />
          <QuestionMarkedMatchingForm onSubmit={onEditMark} onClose={closeFormHandler} marked={questionObj.getMarked()} content={questionObj.getContent()} isOpen={openForm} question={question} />
        </CollapsibleContent>
      </Collapsible>
    </QuestionMarkedWrapper>
  );
}
